import { formatSelector, gameTypesSelector } from '@store/selectors/config';
import { playerHistorySelector } from '@store/selectors/history';
import { gamesLocaleSelector } from '@store/selectors/locale';
import { userSelector } from '@store/selectors/user';
import df from 'dateformat';
import {
  descend, eqBy, last, prop, propOr, sortWith, unionWith,
} from 'ramda';
import { ofType } from 'redux-observable';
import { from, of, concat } from 'rxjs';
import {
  catchError, map, mergeMap, pluck, switchMap,
} from 'rxjs/operators';

import { AMOUNT_TYPES } from '@ezugi/constants';
import { formatAmount, handleError } from '@ezugi/utils';

import { history } from '../../history';
import { notifications } from '../../notifications';
import { getPlayerDayHistory, getPlayerHistory } from './utils';

function errorHandler(error) {
  return concat(
    handleError({
      error,
      failAction: history.failure,
    }),
    of(
      notifications.add({
        message: 'notifications.failed_getting_history',
      }),
    )
  );
}

const sortByRoundId = sortWith([ descend(prop('roundId')) ]);

// for cross browser compatibility
const toISODate = (date, isDay) => (isDay ? date.replace(/-/g, '/') : `${date.replace(/\s/, 'T')}.000Z`);

const localTimeWithTimezoneOffset = (date = new Date()) => {
  const localTime = date.getTime();
  const localOffset = date.getTimezoneOffset() * 60 * 1000; // timezone offset in milliseconds
  return Math.floor((localTime - localOffset) / 1000); // local time seconds since epoch time
};

const parseDateTime = (dateTime, dateFormat, isDay = false) => {
  const dateObj = new Date(toISODate(dateTime ?? '', isDay));
  return {
    key: df(dateObj, 'yyyy-mm-dd'),
    day: df(dateObj, 'dddd'), // Day of the week as its full name.
    dayNo: df(dateObj, 'dd'), // Day of the month as digits; leading zero for single-digit days.
    month: df(dateObj, 'mmmm'), // Month as a three-letter abbreviation.
    date: df(dateObj, dateFormat, true), // convert local time to UTC
    time: df(dateObj, 'HH:MM:ss', true), // convert local time to UTC
  };
};

const historyEpic = (action$, state$) => action$.pipe(
  ofType(history.request),
  switchMap(() => {
    const { identifier } = userSelector(state$.value);
    const { dateFormat = 'yyyy-mm-dd', ...format } = formatSelector(state$.value);
    const playerHistory = playerHistorySelector(state$.value);
    const expandedDay = playerHistory.expandedDay ?? undefined;

    return from(
      getPlayerHistory({
        identifier,
        current_time: localTimeWithTimezoneOffset(), // local time in seconds
      }),
    ).pipe(
      mergeMap(({ result = [] }) => {
        const days = result.map(({ day, rounds }, i) => ({
          ...parseDateTime(day, dateFormat, true),
          index: i,
          rounds: +rounds,
        }));

        const { totalBet, totalWin } = result.reduce(
          (acc, { bet = 0, win = 0 }) => ({
            totalBet: acc.totalBet + Number(bet),
            totalWin: acc.totalWin + Number(win),
          }),
          { totalBet: 0, totalWin: 0 },
        );

        const played = days.reduce((acc, { rounds }) => acc + rounds, 0);

        return of(
          history.set({
            ...playerHistory,
            days: days.reduce((acc, curr) => ({ ...acc, [curr.key]: curr }), {}),
            totalBet: formatAmount(totalBet, AMOUNT_TYPES.BET, format, (result[0] || {}).SessionCurrency || '',),
            totalWin: formatAmount(totalWin, AMOUNT_TYPES.BET, format, (result[0] || {}).SessionCurrency || '',),
            played,
          }),
          ...(expandedDay ? [ history.requestDay({ day: expandedDay }) ] : []),
        );
      },),
      catchError(errorHandler),
    );
  }),
);

const dayHistoryEpic = (action$, state$) => action$.pipe(
  ofType(history.requestDay),
  pluck('payload'),
  switchMap(({ day, loadMore = false }) => {
    const { identifier } = userSelector(state$.value);
    const { dateFormat = 'yyyy-mm-dd', ...format } = formatSelector(state$.value);
    const playerHistory = playerHistorySelector(state$.value);
    const gameTypes = gameTypesSelector(state$.value);
    const gameLocales = gamesLocaleSelector(state$.value);

    const prevRounds = propOr([], day, playerHistory.rounds);
    const oldestRoundParam = loadMore ? { oldest_round_id: last(prevRounds)?.roundId } : {};

    return from(
      getPlayerDayHistory({
        identifier,
        day,
        ...oldestRoundParam,
        current_time: localTimeWithTimezoneOffset(), // local time in seconds
      }),
    ).pipe(
      map(({ result: { player_history } = {} }) => {
        const entries = player_history.map((e, i) => ({
          _bet: e.bet,
          _win: e.win,
          _date: e.date,
          ...parseDateTime(e.date, dateFormat),
          index: i,
          roundId: e.round_id,
          gameId: +e.game_id,
          tableId: e.table_id,
          gameName: gameLocales[`${(gameTypes[+e.game_id] || {})?.name}_${e.table_id}`]
                      || gameLocales[(gameTypes[+e.game_id] || {})?.name]
                      || (gameTypes[+e.game_id]
                      || {})?.server_name || '',
          bet: formatAmount(
            e.bet,
            AMOUNT_TYPES.BET,
            format,
            e.currency,
          ),
          win: formatAmount(
            e.win,
            AMOUNT_TYPES.BET,
            format,
            e.currency,
          ),
          currency: e.currency || '',
        }));

        // combine old rounds list with new rounds list
        const newRounds = unionWith(eqBy(prop('roundId')), entries, prevRounds);

        return history.update({
          rounds: {
            ...playerHistory.rounds,
            [day]: sortByRoundId(newRounds),
          },
          expandedDay: day,
        });
      },),
      catchError(errorHandler),
    );
  }),
);

export { historyEpic, dayHistoryEpic };
